// Artwork for the Symbol Vault.
//
// Symbol names come from `entry.symbols`, which the extraction model writes freely — "Darkness",
// "Bags", "the grizzly bear". That set is unbounded, so a lookup table alone would leave most tiles
// blank. Every name therefore resolves to *something*: a matching piece of art when we have one, and
// otherwise an abstract aura chosen by hashing the name, so an unmapped symbol still looks
// deliberate and keeps the same picture every time it appears.

// Vite fingerprints these into /assets/, which `public/_headers` serves as immutable.
const files = import.meta.glob<string>('../assets/symbols/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

const stemOf = (path: string) => path.split('/').pop()!.replace(/\.webp$/, '');

const named = new Map<string, string>();
const auras: string[] = [];

for (const [path, url] of Object.entries(files)) {
  const stem = stemOf(path);
  if (stem.startsWith('aura-')) auras.push(url);
  else named.set(stem, url);
}

// Auras are picked by hash, so their order has to be stable across builds. Object key order from
// import.meta.glob is not guaranteed to be, hence the explicit sort.
auras.sort();

/**
 * Words that should land on an existing piece of art. Keys are normalised forms, values are the
 * artwork stem. Plain plurals are handled by `normalise`, so only irregular or genuinely different
 * words belong here.
 */
const ALIASES: Record<string, string> = {
  // water vs ocean vs storm all overlap; each word is assigned to exactly one.
  river: 'water', lake: 'water', stream: 'water', pool: 'water', swimming: 'water',
  sea: 'ocean', tide: 'ocean', wave: 'ocean', beach: 'ocean', shore: 'ocean',
  rain: 'storm', thunder: 'storm', lightning: 'storm', cloud: 'storm', sky: 'storm',

  dark: 'darkness', shadow: 'darkness', night: 'darkness', black: 'darkness', void: 'darkness',
  sun: 'light', sunlight: 'light', glow: 'light', brightness: 'light', dawn: 'light',
  gold: 'light', golden: 'light',

  doorway: 'door', gate: 'door', entrance: 'door', threshold: 'door', exit: 'door',
  staircase: 'stairs', stair: 'stairs', step: 'stairs', ladder: 'stairs',
  climbing: 'stairs', descending: 'stairs',

  lock: 'key', unlocking: 'key',
  home: 'house', building: 'house', room: 'house', apartment: 'house', kitchen: 'house',
  luggage: 'bags', suitcase: 'bags', backpack: 'bags', purse: 'bags', packing: 'bags',
  hand: 'arm', reaching: 'arm', touch: 'arm', grip: 'arm',
  shop: 'store', market: 'store', supermarket: 'store', mall: 'store', shopping: 'store',

  reflection: 'mirror', glass: 'mirror',
  serpent: 'snake',
  crow: 'bird', raven: 'bird', owl: 'bird', eagle: 'bird', wing: 'bird',
  flying: 'bird', flight: 'bird', feather: 'bird',
  moonlight: 'moon', lunar: 'moon', star: 'moon',
  wood: 'forest', woods: 'forest', jungle: 'forest', grove: 'forest',
  leaf: 'tree', branch: 'tree', roots: 'tree', oak: 'tree',
  path: 'road', street: 'road', journey: 'road', highway: 'road', trail: 'road', walking: 'road',
  crossing: 'bridge',
  flame: 'fire', burning: 'fire', candle: 'fire', heat: 'fire',
  hill: 'mountain', cliff: 'mountain', peak: 'mountain', summit: 'mountain',
  time: 'clock', watch: 'clock', hourglass: 'clock', clocks: 'clock',
  writing: 'book', letter: 'book', paper: 'book', journal: 'book', page: 'book',
  rose: 'flower', blossom: 'flower', petal: 'flower', plant: 'flower', bloom: 'flower',
  garden: 'flower',
  kitten: 'cat', animal: 'cat', creature: 'cat',

  // ── Landscape ──
  field: 'meadow', grass: 'meadow', grassland: 'meadow', pasture: 'meadow',
  prairie: 'meadow', lawn: 'meadow', clearing: 'meadow',
  sand: 'desert', dune: 'desert', drought: 'desert', wasteland: 'desert', barren: 'desert',
  winter: 'snow', snowfall: 'snow', blizzard: 'snow', snowflake: 'snow',
  frost: 'ice', frozen: 'ice', freezing: 'ice', glacier: 'ice', icicle: 'ice',
  tunnel: 'cave', underground: 'cave', grotto: 'cave', hole: 'cave', pit: 'cave',
  isolation: 'island', marooned: 'island', stranded: 'island',

  // ── Structures ──
  temple: 'church', cathedral: 'church', chapel: 'church', altar: 'church',
  prayer: 'church', worship: 'church', shrine: 'church',
  classroom: 'school', exam: 'school', test: 'school', teacher: 'school',
  student: 'school', university: 'school', college: 'school', homework: 'school',
  doctor: 'hospital', nurse: 'hospital', illness: 'hospital', sick: 'hospital',
  surgery: 'hospital', ambulance: 'hospital', clinic: 'hospital', ward: 'hospital',
  barrier: 'wall', fence: 'wall', brick: 'wall', blockade: 'wall',
  lift: 'elevator',
  bedroom: 'bed', sleep: 'bed', sleeping: 'bed', pillow: 'bed', blanket: 'bed', mattress: 'bed',

  // ── Movement and states ──
  fall: 'falling', dropping: 'falling', plummeting: 'falling', plunge: 'falling',
  drown: 'drowning', sinking: 'drowning', suffocating: 'drowning', underwater: 'drowning',
  trap: 'trapped', cage: 'trapped', stuck: 'trapped', captivity: 'trapped',
  imprisoned: 'trapped', confined: 'trapped', locked: 'trapped',
  losing: 'lost', wandering: 'lost', maze: 'lost', labyrinth: 'lost', directionless: 'lost',
  dying: 'death', dead: 'death', funeral: 'death', grave: 'death', corpse: 'death',
  skull: 'death', coffin: 'death', cemetery: 'death', ending: 'death', mortality: 'death',

  // ── Body and presences ──
  tooth: 'teeth', molar: 'teeth',
  bleeding: 'blood', wound: 'blood', cut: 'blood',
  watching: 'eye', seeing: 'eye', gaze: 'eye', stare: 'eye', iris: 'eye',
  face: 'mask', persona: 'mask', costume: 'mask', disguise: 'mask',
  web: 'spider', cobweb: 'spider',
  pack: 'wolf', howling: 'wolf',
  puppy: 'dog', hound: 'dog',
  stallion: 'horse', mare: 'horse', pony: 'horse', riding: 'horse',
  shark: 'fish', whale: 'fish', dolphin: 'fish',
  moth: 'butterfly', cocoon: 'butterfly', chrysalis: 'butterfly',
  caterpillar: 'butterfly', metamorphosis: 'butterfly',
  baby: 'child', infant: 'child', cradle: 'child', kid: 'child',
  childhood: 'child', son: 'child', daughter: 'child',
  figure: 'stranger', unknown: 'stranger', intruder: 'stranger',
  people: 'crowd', audience: 'crowd', gathering: 'crowd', party: 'crowd', queue: 'crowd',
  birth: 'egg', seed: 'egg', potential: 'egg',

  // ── Objects ──
  vehicle: 'car', driving: 'car', bus: 'car', truck: 'car', taxi: 'car',
  railway: 'train', station: 'train', platform: 'train', subway: 'train', track: 'train',
  ship: 'boat', sailing: 'boat', canoe: 'boat', raft: 'boat', ferry: 'boat',
  telephone: 'phone', call: 'phone', calling: 'phone', ringing: 'phone', message: 'phone',
  coin: 'money', cash: 'money', wealth: 'money', wallet: 'money',
  wedding: 'ring', marriage: 'ring', engagement: 'ring',
};

/** Lowercase, strip anything that is not a letter or space, drop leading articles. */
function normalise(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(the|a|an|my|his|her|their)\s+/, '');
}

/** FNV-1a. Any stable hash works; this one is short and has no dependencies. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function lookup(word: string): string | undefined {
  const direct = named.get(word);
  if (direct) return direct;

  const aliased = ALIASES[word];
  if (aliased) return named.get(aliased);

  return undefined;
}

export type SymbolArt = {
  url: string;
  /** False when this is a hashed aura rather than art that actually depicts the symbol. */
  depicts: boolean;
};

/**
 * Resolve a symbol name to artwork. Never returns undefined as long as at least one aura exists —
 * an unmapped name gets a stable aura rather than an empty tile.
 */
export function symbolArt(rawName: string): SymbolArt | undefined {
  const name = normalise(rawName);

  // Try the whole phrase, then its singular, then each word — "the grizzly bear" has no artwork but
  // "bear" might, and "running water" should find "water".
  const candidates = [name, name.replace(/s$/, ''), ...name.split(' ').flatMap((w) => [w, w.replace(/s$/, '')])];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const url = lookup(candidate);
    if (url) return { url, depicts: true };
  }

  if (auras.length === 0) return undefined;
  // Hash the original name so the same symbol always draws the same aura.
  return { url: auras[hash(name) % auras.length], depicts: false };
}

/**
 * The catalogue stem a free-text name resolves to, when we have art that actually depicts it.
 *
 * Same matching as `symbolArt` — whole phrase, singular, then each word — but it returns the stem
 * rather than the URL, so a chosen cover can be stored as `"forest"` and still find its file after
 * a rebuild fingerprints the asset.
 */
export function artStem(rawName: string): string | undefined {
  const name = normalise(rawName);
  const candidates = [name, name.replace(/s$/, ''), ...name.split(' ').flatMap((w) => [w, w.replace(/s$/, '')])];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (named.has(candidate)) return candidate;
    const aliased = ALIASES[candidate];
    if (aliased && named.has(aliased)) return aliased;
  }

  return undefined;
}

/** The URL for a catalogue stem, or undefined if that piece of art does not exist. */
export function depictingStem(stem: string): string | undefined {
  return named.get(stem);
}

/** Every named piece of art, sorted, for a cover gallery. Auras are omitted — they depict nothing. */
export function namedPieces(): { stem: string; url: string }[] {
  return [...named.entries()]
    .map(([stem, url]) => ({ stem, url }))
    .sort((a, b) => a.stem.localeCompare(b.stem));
}

/** Exposed for tests, so a missing asset directory fails loudly rather than silently falling back. */
export const artCatalogue = { named, auras };
